import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Mail, ArrowRight, KeyRound, Utensils } from "lucide-react";
import api from "@/lib/api";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [authRequestId, setAuthRequestId] = useState("");
  const [step, setStep] = useState("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [devOtp, setDevOtp] = useState("");

  useEffect(() => {
    api.auth.me().then(() => navigate("/admin", { replace: true })).catch(() => {});
  }, [navigate]);

  const handleSendCode = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.auth.sendMagicLink(email);
      setAuthRequestId(data.auth_request_id);
      setDevOtp(data.dev_otp || "");
      setStep("otp");
    } catch (err) {
      setError(err.response?.data?.detail || "Error al enviar el código");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.auth.verifyOtp({ email, otp: otp || devOtp, auth_request_id: authRequestId });
      navigate("/admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Código inválido");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" data-testid="login-visual-panel">
        <img
          src="https://images.pexels.com/photos/3822749/pexels-photo-3822749.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Interior de restaurante"
          className="object-cover w-full h-full"
          data-testid="login-background-image"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-4" data-testid="login-brand-block">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: "#C05C46" }}>
              <Utensils className="h-6 w-6 text-white" />
            </div>
            <span className="text-3xl font-bold font-heading">MenuManager</span>
          </div>
          <p className="text-lg text-white/80 font-body max-w-md" data-testid="login-hero-description">
            Gestiona tus menús de forma fácil y elegante desde un único panel.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8" style={{ background: "#FDFBF7" }}>
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center" data-testid="login-mobile-brand">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#C05C46" }}>
              <Utensils className="h-5 w-5 text-white" />
            </div>
            <span className="text-2xl font-bold font-heading" style={{ color: "#2A2A2A" }}>MenuManager</span>
          </div>

          <Card className="border-[#E6E4DD] shadow-sm" data-testid="login-card">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-heading" style={{ color: "#2A2A2A" }}>
                {step === "email" ? "Iniciar Sesión" : "Verificar Código"}
              </CardTitle>
              <CardDescription className="font-body" style={{ color: "#6B6A65" }}>
                {step === "email" ? "Ingresa tu email para recibir un código de acceso" : `Enviamos un código a ${email}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {step === "email" ? (
                <form onSubmit={handleSendCode} className="space-y-4" data-testid="login-email-form">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-body">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4" style={{ color: "#6B6A65" }} />
                      <Input id="email" type="email" placeholder="tu@email.com" value={email} onChange={(event) => setEmail(event.target.value)} className="pl-10 font-body" required data-testid="login-email-input" />
                    </div>
                  </div>
                  {error && <p className="text-sm" style={{ color: "#D94F3A" }} data-testid="login-error">{error}</p>}
                  <Button type="submit" className="w-full font-body font-semibold" style={{ background: "#C05C46" }} disabled={loading} data-testid="login-send-btn">
                    {loading ? "Enviando..." : "Enviar Código"}
                    {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleVerifyCode} className="space-y-4" data-testid="login-otp-form">
                  <div className="space-y-2">
                    <Label htmlFor="otp" className="font-body">Código de verificación</Label>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-3 h-4 w-4" style={{ color: "#6B6A65" }} />
                      <Input id="otp" type="text" placeholder="000000" value={otp || devOtp} onChange={(event) => setOtp(event.target.value)} className="pl-10 text-center text-xl tracking-[0.3em] font-body" maxLength={6} required data-testid="login-otp-input" />
                    </div>
                  </div>
                  {devOtp && <p className="text-xs text-center p-2 rounded-md" style={{ background: "#FFF3E6", color: "#C05C46" }} data-testid="dev-otp-notice">Modo desarrollo - Código: {devOtp}</p>}
                  {error && <p className="text-sm" style={{ color: "#D94F3A" }} data-testid="login-error">{error}</p>}
                  <Button type="submit" className="w-full font-body font-semibold" style={{ background: "#C05C46" }} disabled={loading} data-testid="login-verify-btn">
                    {loading ? "Verificando..." : "Verificar"}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full font-body" onClick={() => { setStep("email"); setError(""); setOtp(""); setDevOtp(""); }} data-testid="login-back-btn">
                    Cambiar email
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}